import React, { useCallback } from 'react';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { useGraphVisibility } from 'src/app/graph/hooks/useGraphVisibility';

import type { PublicUrlConfirmationModalState } from '../../PublicUrlConfirmationModal';
import {
  INITIAL_CONFIRMED_PUBLIC_URL,
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from '../usePerGraphIdentifierLocalStorage';

export type PublicUrlConfirmation = ReturnType<
  typeof useHasPublicUrlBeenConfirmed
>['hasPublicUrlBeenConfirmed'];

export function useHasPublicUrlBeenConfirmed() {
  const [publicUrlConfirmationModalState, setPublicUrlConfirmationModalState] =
    React.useState<PublicUrlConfirmationModalState | undefined>();

  const isPublicRoute = useGraphVisibility() === 'public';

  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: false,
    shouldOnlyStoreForRegisteredGraphs: true,
  });

  const [confirmedPublicUrls, setConfirmedPublicUrls] =
    usePerGraphIdentifierLocalStorage({
      key: 'hasConfirmedPublicUrl',
      graphIdentifier: localStorageGraphIdentifier,
      stableInitialValue: INITIAL_CONFIRMED_PUBLIC_URL,
    });

  const hasPublicUrlBeenConfirmed = useCallback(
    async ({
      url,
      preflightScript,
      shouldPrompt,
      isSubscription,
      canApproveSingleRun,
    }: {
      url: string | null | undefined;
      preflightScript: string | undefined;
      shouldPrompt: boolean;
      isSubscription: boolean;
      canApproveSingleRun: boolean;
    }) => {
      if (
        !isPublicRoute ||
        !url ||
        !setConfirmedPublicUrls ||
        (confirmedPublicUrls?.url === url &&
          confirmedPublicUrls.preflightScript === preflightScript) ||
        // we don't show this warning for the embeddable explorer
        // since you are on the site of the folks who configured the public graph
        isEmbeddableExplorerRoute()
      ) {
        return true;
      }
      if (!shouldPrompt) {
        return false;
      }
      const urlConfirmed = await new Promise<boolean>(
        (resolvePublicUrlConfirmed) => {
          setPublicUrlConfirmationModalState({
            isOpen: true,
            resolvePublicUrlConfirmed,
            url,
            preflightScript,
            isSubscription,
            canApproveSingleRun,
          });
        },
      );
      setPublicUrlConfirmationModalState(undefined);
      return urlConfirmed;
    },
    [confirmedPublicUrls, setConfirmedPublicUrls, isPublicRoute],
  );

  return React.useMemo(
    () => ({
      hasPublicUrlBeenConfirmed,
      publicUrlConfirmationModalState,
    }),
    [hasPublicUrlBeenConfirmed, publicUrlConfirmationModalState],
  );
}
